import { createContext, useContext, useEffect, useState } from "react";
import { loadRuntimeConfig } from "./runtimeConfig.js";
import ConfigLoadingState from "../pages/ConfigLoadingState.jsx";
import ConfigErrorState from "../pages/ConfigErrorState.jsx";

const RuntimeConfigContext = createContext(null);

/**
 * Loads runtime config exactly once before rendering any child route, and
 * gates every backend-calling screen behind a successful load. This is the
 * boundary that guarantees "no authenticated API call before config is
 * ready"  -  children only ever mount once status === "ready".
 */
export function AppConfigBoundary({ children }) {
  const [state, setState] = useState({ status: "loading", config: null, error: null });

  useEffect(() => {
    let cancelled = false;
    loadRuntimeConfig()
      .then((config) => {
        if (!cancelled) {
          setState({ status: "ready", config, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", config: null, error });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "loading") {
    return <ConfigLoadingState />;
  }

  if (state.status === "error") {
    return <ConfigErrorState error={state.error} />;
  }

  return (
    <RuntimeConfigContext.Provider value={state.config}>{children}</RuntimeConfigContext.Provider>
  );
}

/** Read the loaded runtime config. Only valid inside AppConfigBoundary. */
export function useRuntimeConfig() {
  const config = useContext(RuntimeConfigContext);
  if (!config) {
    throw new Error("useRuntimeConfig must be used within AppConfigBoundary");
  }
  return config;
}
